import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { 
  getEmployeeByEmail, 
  logLoginTime, 
  logLogoutTime, 
  checkAndLogDefaultLogout
} from '../services/firestoreService';

const AuthContext = createContext({});

// Initialize Google Auth Provider
const googleProvider = new GoogleAuthProvider();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to save employee data to cookies and localStorage for backward compatibility
  const saveEmployeeDataToStorage = (employee) => {
    if (!employee) return;
    
    // Save to localStorage
    localStorage.setItem('employeeData', JSON.stringify(employee));
    
    // Save to cookies
    const setCookie = (name, value, days = 7) => {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + days);
      document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expirationDate.toUTCString()}; path=/; secure; samesite=strict`;
    };
    
    const employeeName = employee.name || employee.employeeName;
    if (employeeName) setCookie('employeeName', employeeName);
    if (employee._id) setCookie('employeeId', employee._id);
    if (employee.profilePicture) setCookie('profilePicture', employee.profilePicture);
    if (employee.role) setCookie('role', employee.role);
    if (employee.email) setCookie('email', employee.email);
    if (employee.phoneNumber) setCookie('phoneNumber', employee.phoneNumber);
    if (employee.department) setCookie('department', employee.department);
  };

  // Check if email exists in employees collection
  const checkEmployeeExists = async (email) => {
    try {
      const employee = await getEmployeeByEmail(email);
      return employee;
    } catch (error) {
      console.error('Error checking employee:', error);
      return null;
    }
  };

  // Sign up (create Firebase Auth user for existing employee)
  const signup = async (email, password) => {
    // First check if employee exists in Firestore
    const employee = await checkEmployeeExists(email);
    if (!employee) {
      throw new Error('Email not found in employee records. Please contact administrator.');
    }

    if (employee.isActive === false) {
      throw new Error('Your account is inactive. Please contact administrator.');
    }

    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential;
  };

  // Sign in
  const login = async (email, password) => {
    // First check if employee exists in Firestore
    const employee = await checkEmployeeExists(email);
    if (!employee) {
      throw new Error('Email not found in employee records. Please contact administrator.');
    }

    if (employee.isActive === false) {
      throw new Error('Your account is inactive. Please contact administrator.');
    }

    // Store employee data object (will be used in both Firebase Auth and Firestore password login)
      const employeeDataObj = {
        _id: employee._id,
        name: employee.name || employee.employeeName,
        employeeName: employee.name || employee.employeeName, // Keep for backward compatibility
        email: employee.email,
        role: employee.role,
        profilePicture: employee.profilePicture,
        phoneNumber: employee.phoneNumber || employee.contactNumber,
        department: employee.department,
        extensionNumber: employee.extensionNumber || employee.extension || employee.exten || employee.ext
      };

    try {
      // Try to sign in with Firebase Auth first
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      setEmployeeData(employeeDataObj);
      saveEmployeeDataToStorage(employeeDataObj);

      // Log login time in employee document
      try {
        await logLoginTime(employee._id);
      } catch (error) {
        console.error('Error logging login time:', error);
        // Don't block login if logging fails
      }

      return userCredential;
    } catch (error) {
      // If Firebase Auth fails, try Firestore password authentication
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        // Check if employee has password stored in Firestore
        if (employee.password) {
          // Compare passwords (in production, passwords should be hashed)
          if (employee.password === password) {
            // Password matches - authenticate without Firebase Auth
            console.log('Authenticating with Firestore password for employee:', email);
            
            // Create a mock user object for session management
            const mockUser = {
              uid: employee._id,
              email: employee.email,
              displayName: employee.name || employee.employeeName,
              emailVerified: true
            };
            
            setCurrentUser(mockUser);
            setEmployeeData(employeeDataObj);
            saveEmployeeDataToStorage(employeeDataObj);

            // Log login time in employee document
            try {
              await logLoginTime(employee._id);
            } catch (logError) {
              console.error('Error logging login time:', logError);
              // Don't block login if logging fails
            }

            // Store session in localStorage to maintain auth state
            localStorage.setItem('firestoreAuth', JSON.stringify({
              userId: employee._id,
              email: employee.email,
              timestamp: Date.now()
            }));

            return { user: mockUser };
          } else {
            // Password doesn't match
            throw new Error('Incorrect password. Please check your password and try again.');
          }
        } else {
          // No password in Firestore, try to create Firebase Auth account automatically
          try {
          console.log('Firebase Auth account not found, creating account for employee:', email);
          await createUserWithEmailAndPassword(auth, email, password);
          
          // Now sign in with the newly created account
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          
          setEmployeeData(employeeDataObj);
          saveEmployeeDataToStorage(employeeDataObj);

          return userCredential;
        } catch (createError) {
          // If account creation fails, handle different scenarios
          if (createError.code === 'auth/email-already-in-use') {
            // Account exists but password was wrong, or account was just created
            // Try signing in one more time
            try {
              const userCredential = await signInWithEmailAndPassword(auth, email, password);
              
              setEmployeeData(employeeDataObj);
              saveEmployeeDataToStorage(employeeDataObj);

              return userCredential;
            } catch (signInError) {
              // Password is definitely wrong
              throw new Error('Incorrect password. Please check your password and try again.');
            }
          }
          // If creation fails for other reasons, throw the error
          throw createError;
          }
        }
      }
      // Re-throw other errors
      throw error;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      // Sign in with Google popup
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Check if email exists in employees collection
      const employee = await checkEmployeeExists(user.email);
      if (!employee) {
        // Sign out if employee not found
        await signOut(auth);
        throw new Error('Email not found in employee records. Please contact administrator.');
      }

      if (employee.isActive === false) {
        // Sign out if employee is inactive
        await signOut(auth);
        throw new Error('Your account is inactive. Please contact administrator.');
      }

      // Store employee data (support both new structure: name and old: employeeName)
      const employeeDataObj = {
        _id: employee._id,
        name: employee.name || employee.employeeName,
        employeeName: employee.name || employee.employeeName, // Keep for backward compatibility
        email: employee.email,
        role: employee.role,
        profilePicture: employee.profilePicture,
        phoneNumber: employee.phoneNumber || employee.contactNumber,
        department: employee.department,
        extensionNumber: employee.extensionNumber || employee.extension || employee.exten || employee.ext
      };
      
      setEmployeeData(employeeDataObj);
      saveEmployeeDataToStorage(employeeDataObj);

      // Log login time in employee document
      try {
        await logLoginTime(employee._id);
      } catch (error) {
        console.error('Error logging login time:', error);
        // Don't block login if logging fails
      }

      return result;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    // Log logout time in employee document before signing out
    if (employeeData && employeeData._id) {
      try {
        await logLogoutTime(employeeData._id);
      } catch (error) {
        console.error('Error logging logout time:', error);
        // Don't block logout if logging fails
      }
    }
    
    // Sign out from Firebase Auth if logged in
    try {
    await signOut(auth);
    } catch (error) {
      console.error('Error signing out from Firebase Auth:', error);
    }
    
    // Clear Firestore auth session
    localStorage.removeItem('firestoreAuth');
    
    setCurrentUser(null);
    setEmployeeData(null);
    
    // Clear cookies and localStorage for backward compatibility
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    localStorage.removeItem('employeeData');
  };

  // Monitor auth state
  useEffect(() => {
    // Check for default logout time on mount and periodically
    const checkDefaultLogout = async () => {
      try {
        await checkAndLogDefaultLogout();
      } catch (error) {
        console.error('Error checking default logout:', error);
      }
    };

    // Check immediately
    checkDefaultLogout();
    
    // Check every 5 minutes
    const interval = setInterval(checkDefaultLogout, 5 * 60 * 1000);

    // Check for Firestore auth session on mount
    const checkFirestoreAuth = async () => {
      const firestoreAuth = localStorage.getItem('firestoreAuth');
      if (firestoreAuth) {
        try {
          const authData = JSON.parse(firestoreAuth);
          // Check if session is still valid (24 hours)
          const sessionAge = Date.now() - authData.timestamp;
          if (sessionAge < 24 * 60 * 60 * 1000) {
            const employee = await checkEmployeeExists(authData.email);
            if (employee && employee.isActive !== false) {
              const employeeDataObj = {
                _id: employee._id,
                name: employee.name || employee.employeeName,
                employeeName: employee.name || employee.employeeName,
                email: employee.email,
                role: employee.role,
                profilePicture: employee.profilePicture,
                phoneNumber: employee.phoneNumber || employee.contactNumber,
                department: employee.department,
                extensionNumber: employee.extensionNumber || employee.extension || employee.exten || employee.ext
              };
              
              const mockUser = {
                uid: employee._id,
                email: employee.email,
                displayName: employee.name || employee.employeeName,
                emailVerified: true
              };
              
              setCurrentUser(mockUser);
              setEmployeeData(employeeDataObj);
              saveEmployeeDataToStorage(employeeDataObj);
              setLoading(false);
              return;
            } else {
              // Session invalid or employee not found/inactive
              localStorage.removeItem('firestoreAuth');
            }
          } else {
            // Session expired
            localStorage.removeItem('firestoreAuth');
          }
        } catch (error) {
          console.error('Error checking Firestore auth:', error);
          localStorage.removeItem('firestoreAuth');
        }
      }
    };

    checkFirestoreAuth();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        // Clear Firestore auth if Firebase Auth user exists
        localStorage.removeItem('firestoreAuth');
        
        // Fetch employee data from Firestore
        try {
          const employee = await checkEmployeeExists(user.email);
          if (employee) {
            const employeeDataObj = {
              _id: employee._id,
              name: employee.name || employee.employeeName,
              employeeName: employee.name || employee.employeeName, // Keep for backward compatibility
              email: employee.email,
              role: employee.role,
              profilePicture: employee.profilePicture,
              phoneNumber: employee.phoneNumber || employee.contactNumber,
              department: employee.department,
              extensionNumber: employee.extensionNumber || employee.extension || employee.exten || employee.ext
            };
            
            setEmployeeData(employeeDataObj);
            saveEmployeeDataToStorage(employeeDataObj);
            
            // Log login time if employee is not currently logged in
            // This handles page refresh - if already logged in, don't create duplicate entry
            try {
              if (!employee.isCurrentlyLoggedIn) {
                await logLoginTime(employee._id);
              }
            } catch (error) {
              console.error('Error logging login time:', error);
            }
            
            const employeeName = employee.name || employee.employeeName;
            console.log('Employee data loaded:', {
              _id: employee._id,
              name: employeeName,
              employeeName: employeeName,
              email: employee.email
            });
          } else {
            console.error('Employee not found in Firestore for email:', user.email);
            setEmployeeData(null);
          }
        } catch (error) {
          console.error('Error fetching employee data:', error);
          setEmployeeData(null);
        }
      } else {
        // Check if we have Firestore auth session
        const firestoreAuth = localStorage.getItem('firestoreAuth');
        if (!firestoreAuth) {
        setCurrentUser(null);
        setEmployeeData(null);
        }
      }
      setLoading(false);
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    employeeData,
    login,
    signup,
    signInWithGoogle,
    logout,
    checkEmployeeExists
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

